import React, { useState, useMemo } from 'react';
import { Employee } from '../../types';
import Button from '../Form/Button';
import Input from '../Form/Input';
import Select from '../Form/Select';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { QuickCrewTable, getQuickCrewPrintHtml } from './QuickCrewListSections';

interface QuickCrewListProps {
  onClose: () => void;
}

const QuickCrewList: React.FC<QuickCrewListProps> = ({ onClose }) => {
  const { data: apiEmployees = [] } = useEmployees({ populate: '*' });
  const { data: apiBranches = [] } = useBranches({ populate: '*' });
  
  const employees = useMemo(() => {
    return (apiEmployees as Employee[]).filter(emp => 
      emp.status === 'active' || (emp.status as string) === 'Active'
    );
  }, [apiEmployees]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);

  const filteredEmployees = useMemo(() => {
    let filtered = employees;

    if (searchTerm) {
      filtered = filtered.filter(employee =>
        employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.position.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedDepartment) {
      filtered = filtered.filter(employee => employee.department === selectedDepartment);
    }

    if (selectedBranch) {
      filtered = filtered.filter(employee => employee.branchId === selectedBranch);
    }

    return filtered;
  }, [employees, searchTerm, selectedDepartment, selectedBranch]);

  const departmentOptions = useMemo(() => {
    const uniqueDepts = new Set(employees.map((e: any) => e.department).filter(Boolean));
    return [
      { value: '', label: 'All Departments' },
      ...Array.from(uniqueDepts).sort().map(d => ({ value: d as string, label: d as string }))
    ];
  }, [employees]);

  const branchOptions = useMemo(() => {
    const options = [{ value: '', label: 'All Vessels/HQ' }];
    if (apiBranches && apiBranches.length > 0) {
      apiBranches.forEach((branch: any) => {
        options.push({
          value: branch.documentId || branch.id?.toString() || '',
          label: branch.name || 'Unknown'
        });
      });
    }
    return options;
  }, [apiBranches]);

  const handleEmployeeToggle = (employeeId: string) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map(emp => emp.id));
    }
  };

  const handlePrint = () => {
    const selectedEmployeesData = employees.filter(emp => selectedEmployees.includes(emp.id));
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(getQuickCrewPrintHtml(selectedEmployeesData));
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Quick Active Crew List
            </h2>
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
          <p className="text-gray-600 mt-2">
            Generate and print crew list instantly without saving
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Input
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search employees..."
            />
            <Select
              label="Department"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              options={departmentOptions}
            />
            <Select
              label="Vessel/Headquarters"
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              options={branchOptions}
            />
          </div>

          <div className="flex flex-wrap gap-4 items-center mb-6">
            <Button type="button" variant="secondary" onClick={handleSelectAll}>
              {selectedEmployees.length === filteredEmployees.length ? 'Deselect All' : 'Select All'}
            </Button>
            
            {selectedEmployees.length > 0 && (
              <Button type="button" variant="primary" onClick={handlePrint}>
                Print Selected ({selectedEmployees.length})
              </Button>
            )}
            
            <div className="text-sm text-gray-600">
              {selectedEmployees.length} of {filteredEmployees.length} employees selected
            </div>
          </div>

          <QuickCrewTable
            filteredEmployees={filteredEmployees}
            selectedEmployees={selectedEmployees}
            onToggle={handleEmployeeToggle}
            onSelectAll={handleSelectAll}
          />

          {filteredEmployees.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No employees found matching your criteria
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickCrewList;
